import React, { useState, useEffect } from 'react';
import { useInterestList } from '../context/InterestListContext';
import { getProducts } from '../products';
import { getImage } from '../assets/images';
import { Link } from 'react-router-dom';
import { Trash, Plus, Minus, FileText, ShoppingBag } from 'phosphor-react';
import { API_ENDPOINTS, getFetchConfig } from '../config/api';
import GuestQuoteModal from '../components/GuestQuoteModal';
import './InterestList.css';

const InterestList = () => {
  const {
    interestList,
    removeFromInterestList,
    updateInterestQuantity,
    clearInterestList,
    getTotalItems
  } = useInterestList();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  const [quoteMessage, setQuoteMessage] = useState('');
  const [showGuestModal, setShowGuestModal] = useState(false);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const allProducts = await getProducts();
        setProducts(allProducts);
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Filtrar produtos que estão na lista de interesse
  const interestProducts = products.filter(product => 
    interestList[product.id] > 0
  );

  const handleQuantityChange = (productId, newQuantity) => {
    updateInterestQuantity(productId, Math.max(0, newQuantity));
  };

  const handleClearAll = () => {
    if (window.confirm('Tem certeza que deseja limpar toda a lista de interesse?')) {
      clearInterestList();
    }
  };

  const handleQuickQuote = async () => {
    const token = localStorage.getItem('token');
    
    if (interestProducts.length === 0) {
      setQuoteMessage('❌ Adicione produtos à lista antes de pedir orçamento');
      return;
    }

    if (!token) {
      // Se não está logado, mostrar modal para informações
      setShowGuestModal(true);
      return;
    }

    // Se está logado, proceder normalmente
    await submitQuote();
  };

  const submitQuote = async (guestInfo = null) => {
    setIsSubmittingQuote(true);
    setQuoteMessage('📤 Enviando pedido de orçamento...');

    try {
      // Converter lista de interesse para envio
      const finalInterestList = {};
      interestProducts.forEach(product => {
        finalInterestList[product.id] = interestList[product.id];
      });

      const requestBody = { interestList: finalInterestList };
      
      // Se é utilizador convidado, adicionar informações pessoais
      if (guestInfo) {
        requestBody.guestInfo = guestInfo;
      }

      const endpoint = guestInfo ? API_ENDPOINTS.GUEST_CHECKOUT : API_ENDPOINTS.CHECKOUT;
      const response = await fetch(endpoint, getFetchConfig('POST', requestBody));
      const data = await response.json();

      if (data.status === 'ok') {
        setQuoteMessage(`✅ Pedido de orçamento enviado com sucesso! Receberá um email em breve.`);
        // Limpar lista após sucesso
        setTimeout(() => {
          clearInterestList();
        }, 2000);
      } else {
        setQuoteMessage(`❌ ${data.error || 'Erro ao enviar pedido de orçamento'}`);
      }
    } catch (error) {
      console.error('Erro ao enviar orçamento:', error);
      setQuoteMessage('❌ Erro ao enviar pedido de orçamento. Tente novamente.');
    } finally {
      setIsSubmittingQuote(false);
      setShowGuestModal(false);
      // Limpar mensagem após 5 segundos
      setTimeout(() => {
        setQuoteMessage('');
      }, 5000);
    }
  };

  const getMessageClass = (message) => {
    if (message.includes('✅')) return 'success';
    if (message.includes('❌')) return 'error';
    return 'info';
  };

  if (loading) {
    return (
      <div className="interest-list-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Carregando lista de interesse...</p>
        </div>
      </div>
    );
  }

  const totalItems = getTotalItems();

  return (
    <div className="interest-list-container">
      <div className="interest-list-header">
        <h1>
          <ShoppingBag size={32} />
          Lista de Interesse
        </h1>
        <p className="interest-list-subtitle">
          Produtos selecionados para solicitar orçamento
        </p>
      </div>

      {totalItems === 0 ? (
        <div className="interest-list-empty">
          <ShoppingBag size={64} />
          <h2>Sua lista de interesse está vazia</h2>
          <p>Adicione produtos que deseja incluir no seu orçamento</p>
          <Link to="/shop" className="btn-explore">
            Explorar Produtos
          </Link>
        </div>
      ) : (
        <>
          <div className="interest-list-summary">
            <div className="summary-card">
              <h3>Resumo da Lista</h3>
              <div className="summary-stats">
                <div className="stat">
                  <span className="stat-label">Total de Itens:</span>
                  <span className="stat-value">{totalItems}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Produtos Diferentes:</span>
                  <span className="stat-value">{interestProducts.length}</span>
                </div>
              </div>
              <div className="summary-actions">
                <button 
                  onClick={handleQuickQuote}
                  className="btn-quote-primary"
                  disabled={isSubmittingQuote || totalItems === 0}
                >
                  <FileText size={16} />
                  {isSubmittingQuote ? 'Enviando...' : 'Orçamento Rápido'}
                </button>
                <Link 
                  to="/orcamento/avancado?from=interest-list"
                  className="btn-quote-secondary"
                >
                  <FileText size={16} />
                  Orçamento Completo
                </Link>
                <button 
                  onClick={handleClearAll}
                  className="btn-clear"
                >
                  <Trash size={16} />
                  Limpar Lista
                </button>
              </div>
            </div>
          </div>

          {quoteMessage && (
            <div className={`quote-message ${getMessageClass(quoteMessage)}`}>
              {quoteMessage}
            </div>
          )}

          <div className="interest-list-items">
            {interestProducts.map(product => {
              // Obter a imagem correta usando o sistema de imagens
              const productImage = getImage(product.image);
              
              return (
                <div key={product.id} className="interest-item">
                  <div className="item-image">
                    <img 
                      src={productImage} 
                      alt={product.name}
                      onError={(e) => {
                        e.target.src = getImage('camara.jpg');
                      }}
                    />
                  </div>
                
                <div className="item-details">
                  <h3 className="item-name">{product.name}</h3>
                  <p className="item-category">{product.category}</p>
                  <p className="item-description">{product.description}</p>
                </div>

                <div className="item-quantity">
                  <label htmlFor={`quantity-${product.id}`}>Quantidade:</label>
                  <div className="quantity-controls">
                    <button
                      onClick={() => handleQuantityChange(product.id, interestList[product.id] - 1)}
                      className="quantity-btn"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="quantity-display">
                      {interestList[product.id]}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(product.id, interestList[product.id] + 1)}
                      className="quantity-btn"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                <div className="item-actions">
                  <button
                    onClick={() => removeFromInterestList(product.id)}
                    className="btn-remove"
                    title="Remover da lista"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              );
            })}
          </div>

          <div className="interest-list-footer">
            <div className="quote-section">
              <div className="quote-info">
                <h3>
                  <FileText size={24} />
                  Solicitar Orçamento
                </h3>
                <p>
                  Envie-nos sua lista de interesse e receberá um orçamento 
                  personalizado em até 24 horas.
                </p>
                <ul className="quote-benefits">
                  <li>✅ Orçamento gratuito e sem compromisso</li>
                  <li>✅ Avaliação técnica profissional</li>
                  <li>✅ Preços competitivos</li>
                  <li>✅ Suporte técnico especializado</li>
                </ul>
              </div>
              
              <div className="quote-actions">
                <Link 
                  to="/orcamento/simples" 
                  className="btn-quote-simple"
                >
                  Orçamento Rápido
                </Link>
                <Link 
                  to="/orcamento/avancado" 
                  className="btn-quote-advanced"
                  state={{ selectedProducts: interestList }}
                >
                  Orçamento Detalhado
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      <GuestQuoteModal
        isOpen={showGuestModal}
        onClose={() => setShowGuestModal(false)}
        onSubmit={submitQuote}
        isSubmitting={isSubmittingQuote}
      />
    </div>
  );
};

export default InterestList;
